import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@bms/common';
import { useFlavour } from '@/providers/FlavourProvider';
import { usesGrmsDashboard } from '@/flavours';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

// ============================================================================
// PageHeading — where the user is, in the shell header
//
// Route-driven rather than page-driven: the shell already knows the route, and
// a page that forgets to set a title would otherwise leave the header blank.
// ============================================================================

export interface Crumb {
  label: string;
  /** Omitted on the current page, which is rendered as plain text. */
  to?: string;
}

/**
 * Resolve the header crumbs for a pathname. Exported for the shell only; the
 * routes below are every route the shell wraps (see App.tsx).
 */
export function useRouteCrumbs(pathname: string): Crumb[] {
  const { t } = useTranslation();
  const { flavour } = useFlavour();
  // A hotel's home is the Desk (plan-frontDeskUx D3); every other site keeps its dashboard.
  const dashboard: Crumb = { label: usesGrmsDashboard(flavour) ? t('nav.desk') : t('nav.dashboard'), to: '/' };

  const roomMatch = /^\/rooms\/([^/]+)/.exec(pathname);
  if (roomMatch) {
    return [dashboard, { label: decodeURIComponent(roomMatch[1]) }];
  }
  if (pathname.startsWith('/activity')) {
    return [dashboard, { label: t('nav.activity') }];
  }
  if (pathname.startsWith('/access')) {
    return [{ label: t('nav.accessControl') }];
  }
  if (pathname.startsWith('/housekeeping')) {
    return [{ label: t('nav.housekeeping') }];
  }
  // The Front office group. Its own label is the eyebrow crumb, so a supervisor
  // can see which section of the application they are in, the way the sidebar
  // groups them.
  if (pathname.startsWith('/visitors')) {
    return [{ label: t('nav.frontOffice') }, { label: t('nav.visitors') }];
  }
  if (pathname.startsWith('/cards')) {
    return [{ label: t('nav.frontOffice') }, { label: t('nav.cards') }];
  }
  if (pathname.startsWith('/reports')) {
    return [{ label: t('nav.frontOffice') }, { label: t('nav.reports') }];
  }
  if (pathname.startsWith('/hotel-settings')) {
    return [
      { label: t('nav.frontOffice') },
      { label: t('nav.hotel-settings') },
    ];
  }
  return [{ label: dashboard.label }];
}

export function PageHeading({ crumbs }: { crumbs: Crumb[] }) {
  const currentPage = crumbs[crumbs.length - 1]?.label ?? '';

  return (
    <div className="min-w-0">
      <Breadcrumb>
        <BreadcrumbList className="flex-nowrap gap-1.5 sm:gap-2">
          {crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            return (
              <Fragment key={`${crumb.label}-${index}`}>
                <BreadcrumbItem className="min-w-0">
                  {isLast || !crumb.to ? (
                    <BreadcrumbPage
                      id="page-heading-current"
                      className="truncate text-body font-medium text-foreground"
                    >
                      {crumb.label}
                    </BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild className="truncate text-body font-normal text-muted-foreground">
                      <Link to={crumb.to}>{crumb.label}</Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {!isLast && <BreadcrumbSeparator className="shrink-0" />}
              </Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
      <h1 className="font-display text-page font-bold truncate">{currentPage}</h1>
    </div>
  );
}
